/**
 * Driver Job Delta Sync (Phase 13.8)
 *
 * Additive enhancement to the existing prefetch/snapshot engine.
 *
 * Scope (deliberately narrow — never the whole company database):
 *   - jobs assigned to THIS driver only
 *   - scheduled today or tomorrow, OR already in progress
 *
 * Delta: a snapshot is only re-downloaded when the server `updated_at`
 * differs from the cached snapshot's `updated_at`. Unchanged jobs cost a
 * single lightweight row read.
 *
 * Pruning: jobs that leave the scope (cancelled, reassigned, completed,
 * moved out of the window) are removed from the cache — unless they still
 * have pending queued work or a local draft, which is never discarded.
 *
 * Everything here is read-only against the backend. No schema, storage-path,
 * workflow, API or permission changes.
 */

import { supabase } from "@/integrations/supabase/client";
import { appState, jobsCache, jobDrafts, syncQueue } from "./offlineDb";
import {
  downloadJobSnapshot,
  getAllSnapshots,
  deleteSnapshot,
} from "./jobSnapshotManager";
import { refreshDriverPack } from "./driverOfflinePack";
import { isKnownOffline } from "./connectivity";

export interface DriverSyncResult {
  scoped: number;
  downloaded: number;
  unchanged: number;
  pruned: number;
  failed: number;
  skippedOffline: boolean;
}

const LAST_DELTA_KEY = "driver_delta_sync_at";

function isoDay(offsetDays: number): string {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return d.toISOString().slice(0, 10);
}

/** Jobs the driver may need offline: today, tomorrow, or already started. */
export async function fetchScopedDriverJobs(driverIds: string[]) {
  if (driverIds.length === 0) return [];
  const today = isoDay(0);
  const tomorrow = isoDay(1);

  const { data, error } = await supabase
    .from("jobs")
    .select("id, driver_id, status, scheduled_date, updated_at")
    .in("driver_id", driverIds)
    .is("completed_at", null)
    .in("status", ["pending", "in_progress"])
    .or(
      `status.eq.in_progress,and(scheduled_date.gte.${today},scheduled_date.lte.${tomorrow})`
    )
    .limit(200);

  if (error) throw new Error(error.message);
  return data || [];
}

/**
 * Run one delta sync pass for the signed-in driver.
 * Safe to call repeatedly; no-ops when offline.
 */
export async function syncDriverWorkspace(
  fetchScoped: typeof fetchScopedDriverJobs = fetchScopedDriverJobs
): Promise<DriverSyncResult> {
  const result: DriverSyncResult = {
    scoped: 0,
    downloaded: 0,
    unchanged: 0,
    pruned: 0,
    failed: 0,
    skippedOffline: false,
  };

  if (isKnownOffline()) {
    result.skippedOffline = true;
    return result;
  }

  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return result;

  const pack = await refreshDriverPack(user.id);
  const driverIds = pack?.driver_ids ?? [];
  if (driverIds.length === 0) return result; // not a driver — legacy prefetch path handles admins

  const scoped = await fetchScoped(driverIds);
  result.scoped = scoped.length;

  const snapshots = await getAllSnapshots();
  const snapshotByJob = new Map(snapshots.map((s) => [s.job_id, s]));
  const scopedIds = new Set(scoped.map((j) => j.id as string));

  // ── Download new / changed jobs ────────────────────────────
  for (const job of scoped) {
    if (isKnownOffline()) break;
    const existing = snapshotByJob.get(job.id as string);
    const cachedUpdatedAt = existing?.job?.updated_at ?? null;
    if (existing && cachedUpdatedAt && cachedUpdatedAt === job.updated_at) {
      result.unchanged++;
      continue;
    }
    try {
      await downloadJobSnapshot(job.id as string);
      result.downloaded++;
    } catch (err) {
      result.failed++;
      console.warn(`[DriverSync] Snapshot failed for ${job.id}:`, err);
    }
  }

  // ── Prune out-of-scope jobs (cancelled / reassigned / done) ─
  const [drafts, queued] = await Promise.all([
    jobDrafts.getAll(),
    syncQueue.getAll(),
  ]);
  const protectedJobs = new Set<string>([
    ...drafts.map((d) => d.job_id),
    ...queued.map((q) => q.job_id),
  ]);

  for (const snap of snapshots) {
    if (scopedIds.has(snap.job_id)) continue;
    if (protectedJobs.has(snap.job_id)) continue;
    // Only prune jobs that belong to this driver's cache scope.
    const cachedDriverId = snap.job?.driver_id as string | undefined;
    if (cachedDriverId && !driverIds.includes(cachedDriverId)) {
      // Reassigned away — remove.
    } else if (cachedDriverId && driverIds.includes(cachedDriverId)) {
      // Still ours but out of the today/tomorrow window — remove to keep the
      // cache small; it will be re-downloaded if it re-enters scope.
    }
    await deleteSnapshot(snap.job_id);
    await jobsCache.delete(snap.job_id).catch(() => undefined);
    result.pruned++;
  }

  await appState.setNonEssential(LAST_DELTA_KEY, String(Date.now()));
  console.log(
    `[DriverSync] scoped=${result.scoped} new=${result.downloaded} unchanged=${result.unchanged} pruned=${result.pruned} failed=${result.failed}`
  );
  return result;
}

export async function getLastDriverSyncAt(): Promise<number | null> {
  const raw = await appState.get(LAST_DELTA_KEY);
  return raw ? Number(raw) : null;
}
